import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /**
   * Fix — 17 Aug 2026 (Chris Nelis-Rouse): grace-period timer before actually closing a menu
   * item on pointerleave/blur. Horizon 3.5.0 had this exact mechanism (with a comment noting it
   * was added to stop "flicker/flash" when the pointer moves from one top-level nav item
   * straight into an adjacent one) but 4.1.4 dropped it — `deactivate(event)` used to close
   * immediately. Confirmed live on the storefront: moving the pointer quickly from "Shop" to
   * "Play & Learn" could leave the wrong menu open/closed, because the browser doesn't
   * guarantee "pointerleave Shop" fires before "pointerenter Play & Learn" — whichever handler
   * ran last won, sometimes reopening/closing the wrong item and reading as a blank or
   * stuck-open dropdown. Restoring the grace period (and having `activate()` cancel it below)
   * gives the pointer time to land on the next item before the old one actually closes, so a
   * fast, deliberate hover across the nav reads as one continuous transition instead of a
   * close-then-reopen race. See `deactivate(event)` for the matching half of this fix.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * Fix — 17 Aug 2026 (Chris Nelis-Rouse): see `#reapplyActiveState()` and the `#hydrationObserver`
   * it's paired with, just below, for the full story. In short: this watches for the Section
   * Rendering API "hydration" swap wiping an open dropdown's state, and is the piece that actually
   * catches it in practice (unlike `updatedCallback()` further down, which — despite being the
   * mechanism Horizon provides for exactly this — never actually runs for this particular swap).
   * @type {MutationObserver | null}
   */
  #hydrationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    onDocumentLoaded(this.#applyOpenHeightOnLoad);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): see `#reapplyActiveState()`'s doc comment below for
    // why this exists and why it has to be a MutationObserver rather than `updatedCallback()`.
    this.#hydrationObserver = new MutationObserver(this.#reapplyActiveState);
    const observerOptions = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-active', 'inert'],
    };
    this.#hydrationObserver.observe(this, observerOptions);
    // The overflow ("More") panel renders inside `overflow-list`'s shadow root, which is a
    // separate tree the observer above can't see into even with `subtree: true` — watch it too so
    // an overflow item caught open during the same hydration swap gets the same recovery.
    const overflowRoot = this.refs.overflowMenu?.shadowRoot;
    if (overflowRoot) this.#hydrationObserver.observe(overflowRoot, observerOptions);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): clean up the reinstated grace-period timer too.
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): and the hydration-recovery observer.
    this.#hydrationObserver?.disconnect();
    this.#hydrationObserver = null;
  }

  /**
   * Fix — 17 Aug 2026 (Chris Nelis-Rouse): re-open the active item after the Section Rendering
   * API morphs this component's subtree. `Component.updatedCallback()` (see @theme/component) is
   * *supposed* to fire whenever morph() patches this element's descendants — in particular, this
   * is meant to run after sections/header.liquid's post-load "hydration" swap-in of the full
   * mega-menu content (assets/section-hydration.js), which re-renders the header section shortly
   * after page load to fill in featured products/collections that were skipped on the first
   * render for a faster page load.
   *
   * That hydration fetch always returns the menu in its default, server-rendered CLOSED state
   * (`inert`, no `aria-expanded`, no `data-active`) — so if a visitor opens a dropdown within
   * that first second or two on the page, the morph silently wipes the open submenu's DOM state
   * out from under them: `#state.activeItem` still (correctly) points at the item, but visually
   * the dropdown's contents just vanish while the nav still shows it as active. Confirmed live —
   * this is the "menu loads, then the contents disappear a moment later" report.
   *
   * UPDATE — 17 Aug 2026 (Chris Nelis-Rouse): the previous version of this fix relied entirely on
   * `updatedCallback()` below being called after that swap, the way `@theme/component` documents.
   * It turns out it never is, for this specific swap. `assets/morph.js`'s `onAfterUpdate` hook —
   * the ONLY thing that calls `updatedCallback()` — checks `node instanceof Component`, but `node`
   * there is the freshly-parsed replacement markup (`assets/section-renderer.js` builds it with
   * `new DOMParser().parseFromString(...)`), not the live element already on the page. A node
   * parsed that way is never upgraded to its custom element class, so that check is always false,
   * and `updatedCallback()` silently never runs for ANY custom element after a Section Rendering
   * API update — confirmed directly on the live storefront: patched `updatedCallback` to log,
   * triggered a real hydration re-render, watched the open dropdown's `data-active`/`inert` get
   * wiped exactly as before, and the log never fired. That's a pre-existing gap in Horizon's own
   * `assets/morph.js`, not something this project introduced, and it's out of scope to patch here
   * — that file is shared by every other part of the theme, so changing it needs wider testing
   * than a header-menu fix warrants.
   *
   * So instead of depending on `updatedCallback()`, `#hydrationObserver` (wired up in
   * `connectedCallback()` above) watches the menu directly with the same technique `activate()`
   * already uses just below for a related purpose (catching deferred content injection) — a
   * `MutationObserver`. Confirmed live that the hydration swap's DOM changes are pure attribute
   * removals (`data-active`, `inert`, `aria-expanded`) with no added/removed elements, which is
   * why the observer below has to watch attributes specifically rather than just `childList`.
   * Reads `#state.activeItem` — a private field, untouched by the morph — rather than trying to
   * infer which item was open from the DOM state that the morph just erased.
   *
   * `updatedCallback()` is left in place below calling this same logic, in case Shopify closes
   * the `morph.js` gap in a future theme update and it starts firing for real — at that point it
   * becomes a harmless, redundant no-op alongside the observer rather than something to remember
   * to add back in.
   *
   * Also recomputes the open height afterwards. The swapped-in markup is a different size than
   * what was open before (the "lite" pre-hydration menu vs. the full version with product
   * images), so leaving `--submenu-height` at its old value could crop the newly-revealed content
   * instead of properly showing it. Same double-rAF caution `activate()` already uses below:
   * reading `offsetHeight` in the same tick as the `inert`/`data-active` flip above can return the
   * `content-visibility` placeholder size (`contain-intrinsic-size`, see sections/header.liquid)
   * instead of the real rendered height.
   */
  #reapplyActiveState = () => {
    const item = this.#state.activeItem;
    if (!item || !item.isConnected) return;

    const submenu = findSubmenu(item) ?? (item.closest('[slot="more"]') ? this.overflowMenu : null);
    if (!submenu) return;

    // Already open and intact — nothing to do. Once wired to the MutationObserver above, this
    // runs on every DOM change anywhere in the menu (including the ones it makes itself), not
    // just the one swap it's meant to catch, so bailing out early here keeps it cheap and stops
    // it from re-triggering itself in a loop.
    if (submenu.hasAttribute('data-active') && !submenu.inert) return;

    // Chris Nelis-Rouse — 4 Sep 2026: reaching here means the hydration swap really did
    // replace this component's markup, so every cached measurement was taken against DOM
    // that no longer exists — and the swapped-in "full" menu is a different size than the
    // pre-hydration "lite" one. Drop the caches; #deferredApplyOpenHeight below re-measures.
    this.#clearMeasurementCaches();

    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    submenu.dataset.active = '';
    submenu.inert = false;

    const activeOverflowItem = this.#state.activeOverflowItem;
    if (activeOverflowItem && activeOverflowItem !== item && activeOverflowItem.isConnected) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'true';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = false;
    }

    this.#deferredApplyOpenHeight(item, submenu);
  };

  updatedCallback() {
    super.updatedCallback();
    this.#reapplyActiveState();
  }

  /**
   * Re-run `#applyOpenHeight` after a two-frame wait, only if `item` is still the active one.
   * Fix — 17 Aug 2026 (Chris Nelis-Rouse): shared by `activate()` and `#reapplyActiveState()`,
   * both of which need to correct a height measurement that can be taken before `content-visibility`
   * has made `submenu` relevant/measurable — see the comment where `activate()` calls this for the
   * full story. Same "wait two frames before measuring" approach the theme's own code already uses
   * in `activate()`'s `#submenuMutationObserver`, below, for the same underlying reason.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   */
  #deferredApplyOpenHeight(item, submenu) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Bail if the pointer already moved on to something else in the meantime.
        if (this.#state.activeItem === item) {
          this.#applyOpenHeight(item, submenu);
        }
      });
    });
  }

  /**
   * Fix — 27 Aug 2026 (Chris Nelis-Rouse): re-measure whichever item is currently open once more
   * when the window's `load` event fires (or immediately, if the document is already `complete` —
   * see `onDocumentLoaded`, already imported and already used just above for `#preloadImages`).
   *
   * `#applyOpenHeight`'s synchronous measurement in `activate()`, and even `#deferredApplyOpenHeight`'s
   * two-frame follow-up right above, both assume layout settles within a couple of animation frames.
   * That holds once the page is idle, but confirmed live on a hard refresh: with the rest of the
   * page — unrelated images, webfonts, other deferred scripts — still loading and competing for the
   * main thread, a visitor who opens a top-level item in that window can catch both of those
   * measurements before layout has actually settled, same underlying hazard `#deferredApplyOpenHeight`
   * already patches for the few-frames case, just stretched from frames to seconds. `--submenu-height`
   * and `--full-open-header-height` are driven entirely by these JS measurements (see
   * sections/header.liquid's `.header__underlay-open`, and the `0px` fallback on `#header-component`
   * right above it) — there is no CSS-only fallback — so while they're stuck too small the dropdown's
   * links/images are already visible (a plain CSS rule keyed off `aria-expanded`, set synchronously in
   * `activate()`) but the colored background underlay behind them isn't, and the page behind the menu
   * shows through until something else happens to touch the menu again.
   *
   * The window `load` event is the natural backstop: it only fires once everything the page requested
   * has actually finished, so a re-measurement there is guaranteed to see the settled layout even when
   * every earlier measurement couldn't. No-ops when nothing is open.
   */
  #applyOpenHeightOnLoad = () => {
    const item = this.#state.activeItem;
    if (!item || !item.isConnected) return;

    const submenu = findSubmenu(item) ?? (item.closest('[slot="more"]') ? this.overflowMenu : null);
    this.#applyOpenHeight(item, submenu);
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    // Chris Nelis-Rouse — 4 Sep 2026: viewport width changes both the mega menu's height and
    // the header geometry, so drop the measurement caches rather than reapplying stale
    // values. The next hover re-measures once and re-caches.
    this.#clearMeasurementCaches();
    setHeaderMenuStyle();
  }, 100);

  /**
   * Chris Nelis-Rouse — 4 Sep 2026: forget every cached measurement. Called on resize and
   * whenever the menu's own content is re-rendered underneath us.
   */
  #clearMeasurementCaches() {
    this.#heightCache = new WeakMap();
    this.#boxHeightCache = new WeakMap();
  }

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close the open submenu on Escape and restore focus to its disclosure control.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * Chris Nelis-Rouse — 4 Sep 2026: measured open heights, keyed by menu item.
   *
   * `activate()` writes `submenu.dataset.active` / `submenu.inert = false` — which is what
   * un-skips `content-visibility` on the mega-menu subtree (see sections/header.liquid) —
   * and then `#applyOpenHeight` synchronously reads `submenu.offsetHeight` in the same task.
   * That read forces a full layout of a subtree the browser had deliberately never laid out,
   * which is why the 4 Sep INP report attributed 326ms to a single `menu-list__trigger`
   * hover (5 trigger hovers, mean 195ms, max 432ms).
   *
   * The mega-menu content doesn't change between hovers, so the measurement only has to
   * happen once per item: after that, `#applyCachedOpenHeight` sets the same custom
   * properties with zero layout reads. Correctness is unchanged because
   * `#deferredApplyOpenHeight` — which already ran on every activation — still re-measures
   * two frames later and refreshes this cache, so a stale or wrong value self-corrects after
   * the paint rather than blocking it. First hover of an item behaves exactly as before, so
   * the 17 Aug underlay-height fix is untouched on the path that needed it.
   *
   * @type {WeakMap<HTMLElement, { submenuHeight: number, headerVisibleHeight: number }>}
   */
  #heightCache = new WeakMap();

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    // Chris Nelis-Rouse — 4 Sep 2026: this used to read two getBoundingClientRects on every
    // activation, forcing a second synchronous layout in the same task as the height
    // measurement above. The value is the *distance* between the item's top and the header
    // boundary's bottom, which doesn't change as the page scrolls (a sticky header moves both
    // together) — only a resize or a menu re-render can change it, and both drop the cache.
    // So measure once per item and reuse.
    let boxHeight = this.#boxHeightCache.get(item);

    if (boxHeight === undefined) {
      const rect = item.getBoundingClientRect();
      const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
      const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
      boxHeight = (boundary?.getBoundingClientRect().bottom ?? 0) - rect.top;

      if (boxHeight > 0) this.#boxHeightCache.set(item, boxHeight);
    }

    item.style.setProperty('--box-height', `${boxHeight}px`);
  }

  /**
   * Chris Nelis-Rouse — 4 Sep 2026: measured safety-box heights, keyed by menu item.
   * Cleared alongside #heightCache — see its doc comment.
   * @type {WeakMap<HTMLElement, number>}
   */
  #boxHeightCache = new WeakMap();

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): cancel any pending close now that a menu item is
    // being (re)engaged. This has to run before the `item == this.#state.activeItem` check
    // below — otherwise leaving an item and re-entering it before the grace period in
    // `deactivate(event)` elapses would early-return here (since it's still `#state.activeItem`)
    // without ever canceling that pending close, and the item would incorrectly close anyway.
    // See the matching grace period in `deactivate(event)` and the `#deactivateTimer` doc
    // comment above for the full story.
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
    }

    if (item == this.#state.activeItem) return;

    // Derive the slot context from the list item rather than `event.target`.
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) previousSubmenu.inert = true;
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#expandableFor(previouslyActiveOverflowItem).ariaExpanded = 'false';
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) previousOverflowSubmenu.inert = true;
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';
      // The More trigger auto-opens the first overflow item's submenu, so expose it.
      const activeOverflowSubmenu = findSubmenu(overflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = false;
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        // Chris Nelis-Rouse — 4 Sep 2026: content was just injected into this submenu, so any
        // height cached for this item is stale. Drop it; the #deferredApplyOpenHeight pass
        // already scheduled for this activation (or the next hover) re-measures and re-caches.
        this.#heightCache.delete(item);

        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): extracted to `#applyOpenHeight` so `updatedCallback()`
    // can reuse the same sizing logic after a Section Rendering API morph swaps in differently
    // sized content for the already-open item. See `#applyOpenHeight` for details.
    // Chris Nelis-Rouse — 4 Sep 2026: use the cached measurement when this item has been
    // opened before, so the hover costs no forced layout. On a miss (first open, or after a
    // resize/content change dropped the entry) fall through to the original synchronous
    // measurement, so the path that needed the 17 Aug underlay fix is unchanged.
    const usedCachedHeight = this.#applyCachedOpenHeight(item);
    if (!usedCachedHeight) {
      this.#applyOpenHeight(item, submenu);
    }
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): re-measure a couple of frames later too. The
    // synchronous measurement just above can catch `submenu` before `content-visibility` has
    // actually finished making it relevant/measurable — confirmed live, most easily right after
    // a fresh page load: opening a dropdown could show its links with no background box behind
    // them for a few frames, because the *content* becomes visible immediately via a plain CSS
    // rule keyed off `aria-expanded` (set synchronously just above), while the background is a
    // separate underlay element sized entirely by `--submenu-height`/`--full-open-header-height`
    // — the numbers `#applyOpenHeight` just computed, which this same race can make too small
    // (even 0) on that first read. `#deferredApplyOpenHeight` below re-runs the measurement after
    // the same two-frame wait the theme's own mutation-driven height watcher just above already
    // uses for this identical hazard, correcting the underlay's height without changing anything
    // for the common case where the first measurement was already right.
    //
    // Chris Nelis-Rouse — 4 Sep 2026: only re-measure when the cache didn't already supply
    // the answer. The 4 Sep 14:53 desktop report caught this: the cache removed the
    // synchronous forced layout, but this deferred pass still ran on every activation and
    // forced the same layout one frame later — 229ms across 3 hovers, attributed to
    // header-menu.js inside a FrameRequestCallback. On a cache hit there is nothing to
    // correct, and everything that could make the value stale (resize, injected content, the
    // hydration swap) clears the cache, so the correction still runs where it's needed.
    if (!usedCachedHeight) {
      this.#deferredApplyOpenHeight(item, submenu);
    }
  };

  /**
   * Measure and apply the open height (`--submenu-height` / `--full-open-header-height`) for the
   * given open item and its submenu (or the overflow panel, for overflow/"More" items).
   * Extracted from `activate()` — Fix — 17 Aug 2026 (Chris Nelis-Rouse) — so `updatedCallback()`
   * can re-run the same measurement after a Section Rendering API morph resizes the open item's
   * content (see `updatedCallback()` doc comment).
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   */
  #applyOpenHeight(item, submenu) {
    if (!this.headerComponent) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isDefaultSlot = slot === '';
    const hasSubmenu = Boolean(submenu);

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);

    // Chris Nelis-Rouse — 4 Sep 2026: remember the measurement so repeat hovers of this item
    // can skip the forced layout entirely. Only cache a real height — a 0 here is the
    // content-visibility race this method's own callers exist to correct, and caching it
    // would make that transient bug permanent.
    if (finalHeight > 0) {
      this.#heightCache.set(item, { submenuHeight: finalHeight, headerVisibleHeight });
    }
  }

  /**
   * Chris Nelis-Rouse — 4 Sep 2026
   * Apply a previously measured open height, reading no layout at all.
   * @param {HTMLElement} item
   * @returns {boolean} true when a cached measurement was applied
   */
  #applyCachedOpenHeight(item) {
    if (!this.headerComponent) return false;

    const cached = this.#heightCache.get(item);
    if (!cached) return false;

    this.headerComponent.style.setProperty('--submenu-height', `${cached.submenuHeight}px`);
    this.#setFullOpenHeaderHeight(cached.submenuHeight, cached.headerVisibleHeight);

    return true;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents — i.e. anywhere inside the same list item.
    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Fix — 17 Aug 2026 (Chris Nelis-Rouse): don't close immediately. Capture which item is
    // actually leaving via `event.target` (mirroring how `activate()` derives `item`) rather
    // than reading `this.#state.activeItem` lazily inside the timeout — by the time this fires,
    // a same-tick `activate()` for an adjacent item may already have overwritten that shared
    // state, which is exactly what caused the wrong menu to be closed (or the just-opened one
    // to be closed instead of the one the pointer actually left). Give the pointer a short
    // grace period to land on an adjacent top-level item (or re-enter this same item/submenu);
    // `activate()` cancels this timer as soon as another item is engaged, so this only actually
    // closes the menu when the pointer has genuinely left the whole menu area.
    const itemToDeactivate = findMenuItem(event.target) ?? this.#state.activeItem;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate(itemToDeactivate);
    }, 100);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = true;
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}